export const SESSION_KEYS = {
  REGISTRANT_DASHBOARD_SELECTED_JURISDICTIONS:
    "registrant_dashboard_selected_jurisdictions",
  REGISTRANT_DASHBOARD_SELECTED_LICENSE_TYPES:
    "registrant_dashboard_selected_license_types",
  REGISTRANT_DASHBOARD_IS_MAP_VIEW: "registrant_dashboard_is_map_view",
  REGISTRANT_DASHBOARD_SELECTED_TAB: "registrant_dashboard_selected_tab",
  REGISTRANT_DASHBOARD_LICENSE_COLUMN_FILTERS:
    "registrant_dashboard_license_column_filters",
  REGISTRANT_DASHBOARD_LICENSE_COLUMN_VISIBILITY:
    "registrant_dashboard_license_column_visibility",
  REGISTRANT_DASHBOARD_BULK_COLUMN_FILTERS:
    "registrant_dashboard_bulk_column_filters",
  REGISTRANT_DASHBOARD_BULK_COLUMN_VISIBILITY:
    "registrant_dashboard_bulk_column_visibility",
  REGISTRANT_DASHBOARD_SELECTED_REGISTRANT_ACCOUNT_ID:
    "registrant_dashboard_selected_registrant_account_id",
  REGISTRANT_DASHBOARD_BALANCE_DUE_COHORT_ACTIVE:
    "registrant_dashboard_balance_due_cohort_active",
  REGISTRANT_DASHBOARD_BULK_PAY_LATER_COHORT_ACTIVE:
    "registrant_dashboard_bulk_pay_later_cohort_active",
  REGISTRANT_DASHBOARD_SELECTED_AMS: "registrant_dashboard_selected_ams",
  /** Company question the AMS filter matches on, so other screens filter the same way. */
  REGISTRANT_DASHBOARD_AMS_QUESTION_ID: "registrant_dashboard_ams_question_id",

  /** Operator registrant-portal impersonation target (registrant account UUID string). */
  REGISTRANT_PORTAL_IMPERSONATE_ACCOUNT_ID:
    "registrant_portal_impersonate_account_id",

  /** Operator inspection-portal impersonation target (registrant account UUID string). */
  INSPECTION_PORTAL_IMPERSONATE_ACCOUNT_ID:
    "inspection_portal_impersonate_account_id",

  /** Operator inspection-portal impersonation user (login identity UUID string). */
  INSPECTION_PORTAL_IMPERSONATE_USER_ID:
    "inspection_portal_impersonate_user_id",

  /** Operator OMT/AMS portal impersonation target (party email). */
  OMT_AMS_PORTAL_IMPERSONATE_EMAIL: "omt_ams_portal_impersonate_email",

  /** Operator government-portal impersonation target (login identity UUID string). */
  GOVERNMENT_PORTAL_IMPERSONATE_USER_ID:
    "government_portal_impersonate_user_id",

  /** Jurisdiction of the impersonated government user (UUID string). */
  GOVERNMENT_PORTAL_IMPERSONATE_JURISDICTION_ID:
    "government_portal_impersonate_jurisdiction_id",

  /** Operator title-portal impersonation target (title account UUID string). */
  TITLE_PORTAL_IMPERSONATE_ACCOUNT_ID: "title_portal_impersonate_account_id",

  /**
   * One-shot flag set before Auth0 logout from the inspection SPA so the
   * post-logout landing does not immediately silent-SSO the prior session.
   */
  INSPECTION_SKIP_SILENT_AUTH: "inspection_skip_silent_auth",

  // Shared operator filter keys — used across dashboard, setup, and question config
  OPERATOR_SELECTED_JURISDICTIONS: "operator_selected_jurisdictions",
  OPERATOR_SELECTED_LICENSE_TYPES: "operator_selected_license_types",
  OPERATOR_SELECTED_REGIONS: "operator_selected_regions",

  // Aliases so existing dashboard hook references keep working
  OPERATOR_DASHBOARD_SELECTED_JURISDICTIONS: "operator_selected_jurisdictions",
  OPERATOR_DASHBOARD_SELECTED_LICENSE_TYPES: "operator_selected_license_types",

  OPERATOR_DASHBOARD_IS_MAP_VIEW: "operator_dashboard_is_map_view",
  OPERATOR_DASHBOARD_SELECTED_TAB: "operator_dashboard_selected_tab",
  OPERATOR_DASHBOARD_REGISTRATIONS_COLUMN_FILTERS:
    "operator_dashboard_registrations_column_filters",
  OPERATOR_DASHBOARD_INACTIVE_REGISTRATIONS_COLUMN_FILTERS:
    "operator_dashboard_inactive_registrations_column_filters",

  // Shared government filter keys — used across dashboard, setup, and question config
  GOVERNMENT_SELECTED_JURISDICTIONS: "government_selected_jurisdictions",
  GOVERNMENT_SELECTED_LICENSE_TYPES: "government_selected_license_types",
  GOVERNMENT_SELECTED_REGIONS: "government_selected_regions",

  // Aliases so existing dashboard hook references keep working
  GOVERNMENT_DASHBOARD_SELECTED_LICENSE_TYPES:
    "government_selected_license_types",

  GOVERNMENT_DASHBOARD_IS_MAP_VIEW: "government_dashboard_is_map_view",
  GOVERNMENT_DASHBOARD_SELECTED_TAB: "government_dashboard_selected_tab",
  GOVERNMENT_DASHBOARD_REGISTRATIONS_COLUMN_FILTERS:
    "government_dashboard_registrations_column_filters",
  GOVERNMENT_DASHBOARD_INACTIVE_REGISTRATIONS_COLUMN_FILTERS:
    "government_dashboard_inactive_registrations_column_filters",
  GOVERNMENT_DASHBOARD_UNREGISTERED_PROPERTIES_COLUMN_FILTERS:
    "government_dashboard_unregistered_properties_column_filters",
  OPERATOR_DASHBOARD_UNREGISTERED_PROPERTIES_COLUMN_FILTERS:
    "operator_dashboard_unregistered_properties_column_filters",
  OPERATOR_DASHBOARD_INSPECTIONS_COLUMN_FILTERS:
    "operator_dashboard_inspections_column_filters",
  OPERATOR_DASHBOARD_CHECKS_COLUMN_FILTERS:
    "operator_dashboard_checks_column_filters",
  OPERATOR_DASHBOARD_INVOICES_COLUMN_FILTERS:
    "operator_dashboard_invoices_column_filters",
  OPERATOR_DASHBOARD_REFUNDS_COLUMN_FILTERS:
    "operator_dashboard_refunds_column_filters",
  OPERATOR_DASHBOARD_TITLE_REQUESTS_COLUMN_FILTERS:
    "operator_dashboard_title_requests_column_filters",
  OPERATOR_DASHBOARD_FORECLOSURE_CASES_COLUMN_FILTERS:
    "operator_dashboard_foreclosure_cases_column_filters",
  GOVERNMENT_DASHBOARD_INSPECTIONS_COLUMN_FILTERS:
    "government_dashboard_inspections_column_filters",
  GOVERNMENT_DASHBOARD_FORECLOSURE_CASES_COLUMN_FILTERS:
    "government_dashboard_foreclosure_cases_column_filters",
  OPERATOR_FORECLOSURE_LICENSE_ASSIGNMENT_OPTIONS:
    "operator_foreclosure_license_assignment_options",
  GOVERNMENT_FORECLOSURE_LICENSE_ASSIGNMENT_OPTIONS:
    "government_foreclosure_license_assignment_options",
} as const;

export type SessionKey = (typeof SESSION_KEYS)[keyof typeof SESSION_KEYS];

// Session storage utilities
export const sessionStorage = {
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },
  set: <T>(key: string, value: T): void => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore storage errors
    }
  },
  remove: (key: string): void => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Ignore storage errors
    }
  },
  clear: (): void => {
    try {
      window.sessionStorage.clear();
    } catch {
      // Ignore storage errors
    }
  },
  migrateOldFilters: (): void => {
    try {
      // Clear old filter values to use new default (show all registrations)
      const operatorKey =
        SESSION_KEYS.OPERATOR_DASHBOARD_REGISTRATIONS_COLUMN_FILTERS;
      const governmentKey =
        SESSION_KEYS.GOVERNMENT_DASHBOARD_REGISTRATIONS_COLUMN_FILTERS;

      const operatorFilters = window.sessionStorage.getItem(operatorKey);
      const governmentFilters = window.sessionStorage.getItem(governmentKey);

      // If the stored value has any is_deleted filter, remove it so it uses the new default
      if (operatorFilters) {
        const parsed = JSON.parse(operatorFilters);
        if (parsed.is_deleted) {
          parsed.is_deleted = undefined;
          window.sessionStorage.setItem(operatorKey, JSON.stringify(parsed));
        }
      }

      if (governmentFilters) {
        const parsed = JSON.parse(governmentFilters);
        if (parsed.is_deleted) {
          parsed.is_deleted = undefined;
          window.sessionStorage.setItem(governmentKey, JSON.stringify(parsed));
        }
      }
    } catch {
      // Ignore migration errors
    }
  },
};

/** Column filters that can wedge the dashboard in a bad state after a render crash. */
export const DASHBOARD_COLUMN_FILTER_SESSION_KEYS = [
  SESSION_KEYS.REGISTRANT_DASHBOARD_LICENSE_COLUMN_FILTERS,
  SESSION_KEYS.REGISTRANT_DASHBOARD_BULK_COLUMN_FILTERS,
  SESSION_KEYS.OPERATOR_DASHBOARD_REGISTRATIONS_COLUMN_FILTERS,
  SESSION_KEYS.OPERATOR_DASHBOARD_INACTIVE_REGISTRATIONS_COLUMN_FILTERS,
  SESSION_KEYS.GOVERNMENT_DASHBOARD_REGISTRATIONS_COLUMN_FILTERS,
  SESSION_KEYS.GOVERNMENT_DASHBOARD_INACTIVE_REGISTRATIONS_COLUMN_FILTERS,
  SESSION_KEYS.GOVERNMENT_DASHBOARD_UNREGISTERED_PROPERTIES_COLUMN_FILTERS,
  SESSION_KEYS.OPERATOR_DASHBOARD_UNREGISTERED_PROPERTIES_COLUMN_FILTERS,
  SESSION_KEYS.OPERATOR_DASHBOARD_INSPECTIONS_COLUMN_FILTERS,
  SESSION_KEYS.OPERATOR_DASHBOARD_CHECKS_COLUMN_FILTERS,
  SESSION_KEYS.OPERATOR_DASHBOARD_INVOICES_COLUMN_FILTERS,
  SESSION_KEYS.OPERATOR_DASHBOARD_REFUNDS_COLUMN_FILTERS,
  SESSION_KEYS.OPERATOR_DASHBOARD_TITLE_REQUESTS_COLUMN_FILTERS,
  SESSION_KEYS.OPERATOR_DASHBOARD_FORECLOSURE_CASES_COLUMN_FILTERS,
  SESSION_KEYS.GOVERNMENT_DASHBOARD_INSPECTIONS_COLUMN_FILTERS,
  SESSION_KEYS.GOVERNMENT_DASHBOARD_FORECLOSURE_CASES_COLUMN_FILTERS,
] as const satisfies readonly SessionKey[];

/** Clear persisted table column filters before reloading after an unexpected error. */
export function clearDashboardColumnFiltersFromSession(): void {
  for (const key of DASHBOARD_COLUMN_FILTER_SESSION_KEYS) {
    sessionStorage.remove(key);
  }
}

export function reloadPageAfterUnexpectedError(): void {
  clearDashboardColumnFiltersFromSession();
  window.location.reload();
}
